import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
  Fade,
  ClickAwayListener,
  Stack,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  PaymentOutlined,
  ErrorOutline,
  InfoOutlined,
  LoopOutlined,
  CampaignOutlined,
  AccessTime,
  CancelOutlined,
} from "@mui/icons-material";
import { getHttpClient } from "src/services/rest-api";
import { HTTPService } from "src/services/httpservice";
import { useNavigate } from "react-router-dom";

interface SaasNotification {
  id: number;
  notification_uuid: string;
  type: string;
  title: string;
  body: string;
  action_url: string;
  read_at: string | null;
  createdAt: string;
}

const TYPE_ICON: Record<string, React.ReactElement> = {
  TRIAL_REMINDER: <AccessTime sx={{ fontSize: 16, color: "#F59E0B" }} />,
  PAYMENT_SUCCESS: <PaymentOutlined sx={{ fontSize: 16, color: "#10B981" }} />,
  PAYMENT_FAILED: <ErrorOutline sx={{ fontSize: 16, color: "#EF4444" }} />,
  SUBSCRIPTION_RENEWED: <LoopOutlined sx={{ fontSize: 16, color: "#6366F1" }} />,
  SUBSCRIPTION_CANCELLED: <CancelOutlined sx={{ fontSize: 16, color: "#9CA3AF" }} />,
  PLAN_CHANGED: <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#6366F1" }} />,
  FEATURE_ANNOUNCEMENT: <CampaignOutlined sx={{ fontSize: 16, color: "#3B82F6" }} />,
  SYSTEM: <InfoOutlined sx={{ fontSize: 16, color: "#6B7280" }} />,
};

const TYPE_BG: Record<string, string> = {
  TRIAL_REMINDER: "#FEF3C7",
  PAYMENT_SUCCESS: "#D1FAE5",
  PAYMENT_FAILED: "#FEE2E2",
  SUBSCRIPTION_RENEWED: "#EDE9FE",
  SUBSCRIPTION_CANCELLED: "#F3F4F6",
  PLAN_CHANGED: "#EDE9FE",
  FEATURE_ANNOUNCEMENT: "#DBEAFE",
  SYSTEM: "#F3F4F6",
};

const formatRelative = (date: string) => {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const POLL_INTERVAL_MS = 60000; // Re-fetch every 60s

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<SaasNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getHttpClient(HTTPService.SAAS_NOTIFICATIONS, "GET", null, { limit: 20 }, null);
      setNotifications(res.data?.data || []);
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch {
      // silent fail — notifications are non-critical
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Fetch on mount and on open
  useEffect(() => {
    fetchNotifications(true);
  }, [fetchNotifications]);

  // Poll for new notifications every minute
  useEffect(() => {
    const id = setInterval(() => fetchNotifications(true), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Also fetch when panel opens
  useEffect(() => {
    if (open) fetchNotifications(false);
  }, [open, fetchNotifications]);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const handleMarkRead = useCallback(async (notif: SaasNotification) => {
    if (notif.read_at) return;
    try {
      await getHttpClient(
        `${HTTPService.SAAS_NOTIFICATIONS}/${notif.notification_uuid}/read`,
        "PATCH",
        null,
        null,
        null
      );
      setNotifications((prev) =>
        prev.map((n) => (n.notification_uuid === notif.notification_uuid ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {}

    if (notif.action_url) {
      handleClose();
      navigate(notif.action_url);
    }
  }, [navigate]);

  const handleMarkAllRead = useCallback(async () => {
    setMarkingAll(true);
    try {
      await getHttpClient(`${HTTPService.SAAS_NOTIFICATIONS}/read-all`, "PATCH", null, null, null);
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {} finally {
      setMarkingAll(false);
    }
  }, []);

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          ref={anchorRef}
          id="notification-bell-btn"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={handleToggle}
          size="small"
          sx={{
            width: 36,
            height: 36,
            border: "1px solid #E2E8F0",
            borderRadius: "10px",
            color: "text.secondary",
            "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "transparent" },
          }}
        >
          <Badge
            badgeContent={unreadCount > 99 ? "99+" : unreadCount}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "10px",
                fontWeight: 700,
                minWidth: 16,
                height: 16,
                padding: "0 3px",
                top: -2,
                right: -2,
              },
            }}
          >
            <NotificationsOutlinedIcon sx={{ fontSize: 18 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        style={{ zIndex: 1400 }}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper
              elevation={0}
              sx={{
                width: 360,
                maxHeight: 480,
                borderRadius: "16px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box display="flex" flexDirection="column" sx={{ maxHeight: 480 }}>
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2}
                    py={1.5}
                    sx={{ borderBottom: "1px solid #F1F5F9", bgcolor: "#FAFAFA" }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontSize="14px" fontWeight={700}>Notifications</Typography>
                      {unreadCount > 0 && (
                        <Chip
                          label={unreadCount}
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: "11px", fontWeight: 700 }}
                        />
                      )}
                    </Box>
                    {unreadCount > 0 && (
                      <Button
                        size="small"
                        onClick={handleMarkAllRead}
                        disabled={markingAll}
                        startIcon={markingAll ? <CircularProgress size={10} /> : <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                        sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, color: "primary.main", p: 0.5 }}
                      >
                        Mark all read
                      </Button>
                    )}
                  </Box>

                  {/* Body */}
                  <Box sx={{ overflowY: "auto", flex: 1 }}>
                    {loading ? (
                      <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : notifications.length === 0 ? (
                      <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={1}>
                        <NotificationsOutlinedIcon sx={{ fontSize: 36, color: "#CBD5E1" }} />
                        <Typography fontSize="13px" color="text.secondary">No notifications yet</Typography>
                      </Box>
                    ) : (
                      <Stack divider={<Divider />}>
                        {notifications.map((notif) => (
                          <Box
                            key={notif.notification_uuid}
                            onClick={() => handleMarkRead(notif)}
                            sx={{
                              px: 2,
                              py: 1.5,
                              cursor: "pointer",
                              bgcolor: notif.read_at ? "#fff" : "#F8FAFF",
                              borderLeft: notif.read_at ? "3px solid transparent" : "3px solid #6366F1",
                              transition: "background 0.15s",
                              "&:hover": { bgcolor: "#F1F5F9" },
                            }}
                          >
                            <Box display="flex" gap={1.5} alignItems="flex-start">
                              <Box
                                sx={{
                                  mt: 0.25,
                                  width: 28,
                                  height: 28,
                                  borderRadius: "8px",
                                  bgcolor: TYPE_BG[notif.type] || "#F3F4F6",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {TYPE_ICON[notif.type] || <InfoOutlined sx={{ fontSize: 16 }} />}
                              </Box>
                              <Box flex={1} minWidth={0}>
                                <Typography
                                  fontSize="13px"
                                  fontWeight={notif.read_at ? 500 : 700}
                                  lineHeight={1.3}
                                  noWrap={false}
                                >
                                  {notif.title}
                                </Typography>
                                {notif.body && (
                                  <Typography fontSize="12px" color="text.secondary" mt={0.25} lineHeight={1.4}>
                                    {notif.body}
                                  </Typography>
                                )}
                                <Typography fontSize="11px" color="text.disabled" mt={0.5}>
                                  {formatRelative(notif.createdAt)}
                                </Typography>
                              </Box>
                              {!notif.read_at && (
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    bgcolor: "#6366F1",
                                    borderRadius: "50%",
                                    mt: 0.75,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{ borderTop: "1px solid #F1F5F9", px: 2, py: 1, bgcolor: "#FAFAFA" }}
                    display="flex"
                    justifyContent="center"
                  >
                    <Button
                      size="small"
                      onClick={() => { handleClose(); navigate("/settings"); }}
                      sx={{ textTransform: "none", fontSize: "12px", fontWeight: 600, color: "text.secondary" }}
                    >
                      View billing settings →
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default NotificationBell;
